import { imin, withinRange } from '../math/fixed.ts';
import type { BattleState } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { BedRules, BedState } from '../roca-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { setOpen } from './isla-ice.ts';
import { spawnCub } from './mouths.ts';
import { allyStrike, rocaNpc } from './roca-ally.ts';

/**
 * The ash bed (docs/sendas/barbaro.md, chapter 5, act 3: «La cama de ceniza»; the ground in battle/roca-layout.ts): down
 * from the cave's mouth through two chambers under a low roof, grey ash on the floor like dirty snow, boar and seal bones,
 * to the bed of ash at the back (its chamber's band down). Coming into a chamber, its band comes out of the side cracks: the cubs (small, many,
 * frail) and what is left of the pack; its way on is shut by ash and bones until the band is down. Ulla strikes beside
 * the hero. Won at the bed.
 *
 * Cost: three bands; the gates the grid's plugs, set only as one opens.
 */

const br = (s: BattleState): BedRules | undefined => s.rules?.senda?.roca?.bed;
const bs = (s: BattleState): BedState | undefined => s.senda?.roca?.bed;

const gates = (s: BattleState, r: BedRules, w: BedState): void => setOpen(s, r.rooms.slice(w.cleared).map((m) => m.gate));

const fresh = (s: BattleState, r: BedRules): BedState => {
  const w: BedState = { room: -1, band: [], parts: 0, partAt: -1, cleared: 0, ullaCd: 0, done: -1 };
  gates(s, r, w);
  return w;
};

/** Half a room's band out of its side cracks. */
const part = (s: BattleState, r: BedRules, w: BedState): void => {
  const room = r.rooms[w.room];
  const band = r.bands[w.room];
  if (!room || !band) return;
  const half = (n: number): number => (w.parts === 0 ? n - (n >> 1) : n >> 1);
  const cubs = half(band[0]);
  const wolves = half(band[1]);
  for (let i = 0; i < cubs + wolves; i++) {
    const p = room.holes[(i + w.parts) % room.holes.length] as Pt;
    const x = p.x + ((i % 3) - 1) * 360;
    const y = p.y + ((i >> 2) % 2) * 320;
    const before = s.nextId;
    if (i < cubs) spawnCub(s, x, y, r.cubHp, r.cubDmgBp);
    else spawnEnemy(s, 'wolf', 1, false, 0, x, y, r.wolfHp);
    w.band.push(before);
  }
  w.parts++;
  w.partAt = w.parts < 2 ? s.tick + r.part : -1;
};

export const bedTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = br(s);
  const st = s.senda;
  if (!r || !st) return;
  const rs = (st.roca ??= {});
  const w = (rs.bed ??= fresh(s, r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // (The last chamber's band down too: the bed is in it.)
  if (h && w.cleared >= r.rooms.length && withinRange(h.x, h.y, r.bed.x, r.bed.y, r.reach)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'rBed', npc: '', x: r.bed.x, y: r.bed.y });
    cue('bed');
    return;
  }
  // Into the next room: its band.
  const next = w.room + 1;
  const room = r.rooms[next];
  if (h && room && next <= w.cleared && withinRange(h.x, h.y, room.at.x, room.at.y, room.r - 300)) {
    w.room = next;
    w.band = [];
    w.parts = 0;
    part(s, r, w);
    ev(s, { t: 'senda', kind: 'rRoom', npc: '', x: room.at.x, y: room.at.y, n: next });
    cue(`room${next + 1}`);
  }
  if (w.partAt >= 0 && s.tick >= w.partAt) part(s, r, w);
  // The band down: the way on opens.
  if (w.room >= 0 && w.cleared === w.room && w.parts >= 2 && s.tick % 6 === 0 && !s.enemies.some((e) => e.hp > 0 && w.band.includes(e.id))) {
    w.cleared = imin(r.rooms.length, w.room + 1);
    gates(s, r, w);
    const g = r.rooms[w.room] as BedRoom;
    ev(s, { t: 'senda', kind: 'rOpen', npc: '', x: g.at.x, y: g.at.y, n: w.room });
    cue(`open${w.room + 1}`);
  }
  w.ullaCd = allyStrike(s, rocaNpc(s, 'ulla'), r.ulla, w.ullaCd);
};

type BedRoom = BedRules['rooms'][number];

/** Chambers passed, of the three to the bed. */
export const bedGoal = (s: BattleState): { done: number; need: number } => {
  const r = br(s);
  const w = bs(s);
  const need = r?.rooms.length ?? 3;
  return { done: w ? (w.done >= 0 ? need : imin(need, w.cleared)) : 0, need };
};

export const bedResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = br(s);
  const w = bs(s);
  return r && w && w.done >= 0 && s.tick >= w.done + r.settle ? 'won' : null;
};
